import React, { useCallback, useMemo } from 'react';
import { Avatar, Box, CircularProgress, Paper } from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';

import { getAvatarUrl } from '../../helpers';
import { User } from '../../types';
import styles from './style';

interface MentionSuggestionsProps {
  // The text typed after the @.
  query: string;
  users: User[];
  loading: boolean;
  activeIndex: number;
  // Usernames already checked for this @ (a click unchecks them).
  checked: string[];
  onPick: (user: User) => void;
  onHover: (index: number) => void;
  onEndReached: () => void;
}

// The next page is loaded at 70% of the scroll, like the app.
const LOAD_MORE_RATIO = 0.7;

// Users proposed above the comment field while typing @.
const MentionSuggestions: React.FC<MentionSuggestionsProps> = React.memo((props) => {
  const {
    query,
    users,
    loading,
    activeIndex,
    checked,
    onPick,
    onHover,
    onEndReached,
  } = props;

  const onClick = useCallback((event: React.MouseEvent<HTMLElement>) => {
    const picked = users.find((u) => u.id === event.currentTarget.dataset.id);
    if (picked) {
      onPick(picked);
    }
  }, [onPick, users]);

  const onMouseEnter = useCallback((event: React.MouseEvent<HTMLElement>) => {
    onHover(Number(event.currentTarget.dataset.index));
  }, [onHover]);

  // The click must keep the focus (and the caret) in the field.
  const onMouseDown = useCallback((event: React.MouseEvent) => {
    event.preventDefault();
  }, []);

  const onScroll = useCallback((event: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, clientHeight, scrollHeight } = event.currentTarget;
    if (scrollTop + clientHeight >= scrollHeight * LOAD_MORE_RATIO) {
      onEndReached();
    }
  }, [onEndReached]);

  const lowerQuery = useMemo(() => query.toLowerCase(), [query]);
  const lowerChecked = useMemo(() => checked.map((u) => u.toLowerCase()), [checked]);

  // Like the app: nothing when no one matches, a spinner while the first page loads.
  if (!users.length) {
    return loading ? (
      <Paper sx={styles.root}>
        <Box sx={styles.loading}>
          <CircularProgress size={18} sx={styles.spinner} />
        </Box>
      </Paper>
    ) : null;
  }

  return (
    <Paper role="listbox" onScroll={onScroll} sx={styles.root}>
      {users.map((u, index) => {
        const isPrefix = lowerQuery && u.username.toLowerCase().startsWith(lowerQuery);
        return (
          <Box
            key={u.id}
            component="button"
            type="button"
            role="option"
            aria-selected={index === activeIndex}
            data-id={u.id}
            data-index={index}
            onMouseDown={onMouseDown}
            onMouseEnter={onMouseEnter}
            onClick={onClick}
            sx={index === activeIndex ? styles.rowActive : styles.row}
          >
            <Avatar alt="" src={getAvatarUrl(u.avatar)} sx={styles.avatar} />
            <Box sx={styles.texts}>
              <Box component="span" sx={styles.username}>
                {isPrefix ? (
                  <>
                    <Box component="span" sx={styles.match}>{u.username.slice(0, query.length)}</Box>
                    {u.username.slice(query.length)}
                  </>
                ) : u.username}
              </Box>
              <Box component="span" sx={styles.handle}>{`@${u.username}`}</Box>
            </Box>
            {lowerChecked.includes(u.username.toLowerCase())
              ? <CheckCircle sx={styles.check} />
              : <Box component="span" sx={styles.round} />}
          </Box>
        );
      })}
    </Paper>
  );
});

export default MentionSuggestions;
